import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, LayoutGrid, List, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["S","M","T","W","T","F","S"];

export default function Events() {
  const { user } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [view, setView] = useState("list"); // list | calendar
  const [cursor, setCursor] = useState(() => new Date());

  useEffect(() => {
    api.get("/events", { params: { upcoming: true } }).then(r => setUpcoming(r.data)).catch(() => {});
    api.get("/events", { params: { upcoming: false } }).then(r => setPast(r.data)).catch(() => {});
  }, []);

  const register = async (id) => {
    if (!user) { toast("Sign in to register"); return; }
    try {
      await api.post("/events/register", { event_id: id });
      toast.success("You're registered.");
    } catch { toast.error("Failed to register"); }
  };

  // Build a 6x7 calendar grid for cursor month
  const grid = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const start = new Date(y, m, 1 - first.getDay());
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    upcoming.forEach(e => {
      const k = e.date.slice(0, 10);
      (map[k] = map[k] || []).push(e);
    });
    return map;
  }, [upcoming]);

  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="events-page">
      <section className="section-pad container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="overline">Events & Workshops</p>
            <h1 className="font-display text-5xl md:text-6xl text-aubergine mt-5 leading-tight">
              Curated rooms. <em className="text-rose not-italic">Unscripted conversations.</em>
            </h1>
          </div>
          <div className="lg:col-span-4 flex justify-start lg:justify-end gap-2">
            <button data-testid="ev-view-list" onClick={() => setView("list")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest border ${view === "list" ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum"}`}>
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button data-testid="ev-view-cal" onClick={() => setView("calendar")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest border ${view === "calendar" ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum"}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Calendar
            </button>
          </div>
        </div>

        {/* UPCOMING — list or calendar */}
        {view === "list" ? (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcoming.map(ev => <EventCard key={ev.id} ev={ev} onRsvp={register} />)}
            {upcoming.length === 0 && <p className="md:col-span-3 text-center py-12 text-plumSoft">No upcoming events.</p>}
          </div>
        ) : (
          <div className="mt-10 card-luxe p-5 md:p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl text-aubergine">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
              <div className="flex items-center gap-2">
                <button data-testid="cal-prev" onClick={prevMonth} className="w-9 h-9 rounded-full border border-aubergine/20 text-aubergine flex items-center justify-center hover:bg-aubergine hover:text-ivory transition-all"><ChevronLeft className="w-4 h-4" /></button>
                <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="text-xs uppercase tracking-widest text-plum link-underline px-3">Today</button>
                <button data-testid="cal-next" onClick={nextMonth} className="w-9 h-9 rounded-full border border-aubergine/20 text-aubergine flex items-center justify-center hover:bg-aubergine hover:text-ivory transition-all"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {DOW.map((d, i) => <div key={i} className="text-[10px] uppercase tracking-widest text-plum text-center py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {grid.map((d, i) => {
                const key = d.toISOString().slice(0, 10);
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = key === todayKey;
                const dayEvents = eventsByDate[key] || [];
                return (
                  <div key={i} className={`min-h-[88px] p-2 rounded-lg border ${inMonth ? "bg-white border-aubergine/8" : "bg-lavender/30 border-transparent"} ${isToday ? "!border-rose !border-2" : ""}`}>
                    <p className={`text-xs font-medium ${inMonth ? "text-aubergine" : "text-plumSoft/40"} ${isToday ? "text-rose" : ""}`}>{d.getDate()}</p>
                    <div className="mt-1 space-y-1">
                      {dayEvents.slice(0, 2).map(ev => (
                        <a key={ev.id} href={`#ev-${ev.id}`} className="block text-[10px] leading-tight bg-rose/15 text-aubergine rounded px-1.5 py-1 truncate" title={ev.title}>
                          {ev.title}
                        </a>
                      ))}
                      {dayEvents.length > 2 && <span className="text-[10px] text-plum">+{dayEvents.length - 2} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* List below calendar for selected month */}
            <div className="mt-8 space-y-3">
              {upcoming.filter(ev => {
                const d = new Date(ev.date);
                return d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear();
              }).map(ev => (
                <div key={ev.id} id={`ev-${ev.id}`} className="flex flex-wrap items-center justify-between gap-3 py-3 border-t border-aubergine/8">
                  <div>
                    <p className="font-display text-lg text-aubergine">{ev.title}</p>
                    <p className="text-xs text-plum mt-0.5">{new Date(ev.date).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · {ev.location}</p>
                  </div>
                  <button onClick={() => register(ev.id)} className="btn-primary !py-2 text-xs">RSVP</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PAST EVENTS — Gallery */}
        {past.length > 0 && (
          <div className="mt-20">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="overline">Past Events</p>
                <h2 className="font-display text-3xl md:text-4xl text-aubergine mt-3">From the WOWSHE archives.</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {past.map(ev => (
                <Link key={ev.id} to={`/events/${ev.id}/gallery`} data-testid={`past-event-${ev.id}`} className="card-luxe overflow-hidden group">
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img src={ev.image} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-aubergine/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-ivory">
                      <div>
                        <p className="text-[10px] tracking-widest uppercase text-champagne">Past · {new Date(ev.date).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
                        <h3 className="font-display text-2xl mt-1 leading-tight">{ev.title}</h3>
                      </div>
                      <span className="flex items-center gap-1.5 bg-ivory/15 backdrop-blur rounded-full px-3 py-1.5 text-xs">
                        <Camera className="w-3 h-3" /> {(ev.photos || []).length}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function EventCard({ ev, onRsvp }) {
  return (
    <article data-testid={`event-card-${ev.id}`} className="card-luxe overflow-hidden flex flex-col">
      <div className="aspect-[4/3] overflow-hidden relative">
        <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />
        <span className="absolute top-4 left-4 bg-ivory/90 backdrop-blur px-3 py-1 text-[10px] tracking-widest uppercase rounded-full text-aubergine">{ev.type}</span>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-2xl text-aubergine leading-snug">{ev.title}</h3>
        <p className="text-sm text-plumSoft mt-3 font-light leading-relaxed line-clamp-3">{ev.description}</p>
        <div className="mt-5 space-y-1.5 text-xs text-plum">
          <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /><span>{new Date(ev.date).toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" })}</span></div>
          <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /><span>{ev.location}</span></div>
          <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /><span>{(ev.attendees || []).length} attending</span></div>
        </div>
        <button data-testid={`event-rsvp-${ev.id}`} onClick={() => onRsvp(ev.id)} className="mt-6 btn-primary self-start">RSVP</button>
      </div>
    </article>
  );
}
